// Resolves a new launch's map coordinates.
// 1) Verified static table (data/launchCoordinates.js)  2) runtime SLA OneMap geocode for newly added projects.
import { useEffect, useState } from "react";
import LAUNCH_COORDINATES from "@/data/launchCoordinates";

const ONEMAP = "https://www.onemap.gov.sg/api/common/elastic/search?returnGeom=Y&getAddrDetails=Y&pageNum=1&searchVal=";
const CACHE_PREFIX = "asp_geo_v1_";

const inSingapore = (lat, lng) => lat > 1.15 && lat < 1.48 && lng > 103.6 && lng < 104.1;
const norm = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
const asciiTitle = (t) => String(t || "").replace(/[^\x20-\x7E]/g, " ").replace(/\s+-\s+Singapore\s*$/i, "").replace(/\s+/g, " ").trim();

function cleanAddress(addr) {
  let s = String(addr || "").split("\n")[0];
  s = s.replace(/\(.*?\)/g, " ").replace(/S\(?\d{6}\)?/g, " ").replace(/singapore\s*\d*/gi, " ");
  s = s.split(/\s+to\s+\d|\),/i)[0];
  // "1 ,3, 5 Marina Garden Lane" -> "1 Marina Garden Lane"
  const m = s.match(/^\s*(?:blk\s*)?(\d+[A-Za-z]?)((?:\s*(?:,|&|\/|and)\s*\d+[A-Za-z]?)*)\s+(.+?)[\s,]*$/i);
  if (m) return `${m[1]} ${m[3]}`.replace(/\s+/g, " ").trim();
  return s.replace(/[\s,]+$/, "").replace(/\s+/g, " ").trim();
}

async function search(q) {
  if (!q) return [];
  const res = await fetch(ONEMAP + encodeURIComponent(q));
  if (!res.ok) return [];
  const j = await res.json();
  return (j.results || [])
    .map((x) => ({ ...x, lat: Number(x.LATITUDE), lng: Number(x.LONGITUDE) }))
    .filter((x) => inSingapore(x.lat, x.lng));
}

async function geocodeLaunch(launch) {
  const title = norm(asciiTitle(launch.title));
  const building = (x) => norm(x.BUILDING).replace(/ U C$/, "");

  // Project name, accepted only when OneMap's building name matches.
  const byName = await search(asciiTitle(launch.title));
  let hit = byName.find((x) => building(x) && (building(x).startsWith(title) || title.startsWith(building(x))));

  // Postal code in the address.
  const postal = (String(launch.address || "").match(/\b(\d{6})\b/) || [])[1];
  if (!hit && postal) hit = (await search(postal))[0];

  // Street address, preferring a result on the same road.
  if (!hit && launch.address) {
    const a = cleanAddress(launch.address);
    const road = norm(a.replace(/^\d+[A-Za-z]?\s+/, ""));
    const res = await search(a);
    hit = res.find((x) => norm(x.ROAD_NAME).includes(road) || road.includes(norm(x.ROAD_NAME))) || res[0];
  }
  return hit ? { lat: hit.lat, lng: hit.lng } : null;
}

function slugOf(launch) {
  return launch?.slug?.current || launch?.slug || launch?._id || "";
}

/** Returns { lat, lng } for a launch, or null while resolving / if not found. */
export function useLaunchCoordinates(launch) {
  const slug = slugOf(launch);
  const known = LAUNCH_COORDINATES[slug] || null;
  const [coords, setCoords] = useState(known);

  useEffect(() => {
    if (!launch) return;
    if (LAUNCH_COORDINATES[slug]) { setCoords(LAUNCH_COORDINATES[slug]); return; }

    const key = CACHE_PREFIX + slug;
    try {
      const c = JSON.parse(localStorage.getItem(key) || "null");
      if (c && inSingapore(c.lat, c.lng)) { setCoords(c); return; }
    } catch (_) { /* ignore */ }

    let cancelled = false;
    setCoords(null);
    geocodeLaunch(launch)
      .then((c) => {
        if (cancelled || !c) return;
        setCoords(c);
        try { localStorage.setItem(key, JSON.stringify(c)); } catch (_) { /* ignore */ }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, launch?.address, launch?.title]);

  return coords;
}
